import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  CheckCircle as VerifyIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  MedicalServices as ServicesIcon,
  Star as FeaturedIcon,
  StarBorder as UnfeaturedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as UnverifyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import apiService from '../../services/api';
import { ProfessionalService, ServiceCategory, VerifyServiceRequest } from '../../types';

const ProfessionalServicesPage: React.FC = () => {
  const [services, setServices] = useState<ProfessionalService[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'pending' | 'verified'>('pending');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number>(0);
  const [verificationFilter, setVerificationFilter] = useState<'all' | 'verified' | 'pending'>('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'unavailable'>('all');
  const [featuredFilter, setFeaturedFilter] = useState<'all' | 'featured' | 'not_featured'>('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedService, setSelectedService] = useState<ProfessionalService | null>(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [unverifyReason, setUnverifyReason] = useState('');
  const [unverifyDialogOpen, setUnverifyDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    service_name: '',
    description: '',
    price: '',
    duration_minutes: 60,
    experience_years: 0,
    specializations: [] as string[],
    service_category_id: 0,
    is_available: true,
    is_featured: false,
  });

  useEffect(() => {
    fetchData(viewMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Filter services based on search query and filters
  const filteredServices = useMemo(() => {
    let filtered = services;

    // Filter by search query (multiple fields)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(service => 
        service.service_name.toLowerCase().includes(query) ||
        service.description.toLowerCase().includes(query) ||
        (service.professional?.name && service.professional.name.toLowerCase().includes(query)) ||
        (service.professional?.email && service.professional.email.toLowerCase().includes(query)) ||
        (service.professional?.profile?.city && service.professional.profile.city.toLowerCase().includes(query)) ||
        (service.specializations && service.specializations.some(spec => spec.toLowerCase().includes(query)))
      );
    }

    // Filter by category
    if (categoryFilter > 0) {
      filtered = filtered.filter(service => service.service_category_id === categoryFilter);
    }

    // Filter by verification status
    if (verificationFilter === 'verified') {
      filtered = filtered.filter(service => service.is_verified);
    } else if (verificationFilter === 'pending') {
      filtered = filtered.filter(service => !service.is_verified);
    }

    // Filter by availability
    if (availabilityFilter === 'available') {
      filtered = filtered.filter(service => service.is_available);
    } else if (availabilityFilter === 'unavailable') {
      filtered = filtered.filter(service => !service.is_available);
    }

    // Filter by featured status
    if (featuredFilter === 'featured') {
      filtered = filtered.filter(service => service.is_featured);
    } else if (featuredFilter === 'not_featured') {
      filtered = filtered.filter(service => !service.is_featured);
    }

    return filtered;
  }, [services, searchQuery, categoryFilter, verificationFilter, availabilityFilter, featuredFilter]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter(0);
    setVerificationFilter('all');
    setAvailabilityFilter('all');
    setFeaturedFilter('all');
  };

  const hasActiveFilters = searchQuery.trim() !== '' || categoryFilter > 0 || verificationFilter !== 'all' || availabilityFilter !== 'all' || featuredFilter !== 'all';

  const fetchData = async (mode: 'pending' | 'verified' = viewMode) => {
    try {
      setIsLoading(true);
      setError('');
      // per_page set high enough to fetch every record in one request — both endpoints
      // support pagination but have no server-side max cap, and this page's search/filter
      // UI operates client-side over the full `services` array, so all records must be loaded.
      const [servicesData, categoriesData] = await Promise.all([
        mode === 'pending'
          ? apiService.getPendingVerificationServices({ per_page: 5000 })
          : apiService.getProfessionalServices({ per_page: 5000, admin_view: true }),
        apiService.getServiceCategories(),
      ]);
      
      // Ensure services is always an array
      const servicesArray = servicesData?.data || [];
      const categoriesArray = categoriesData || [];
      
      setServices(Array.isArray(servicesArray) ? servicesArray : []);
      setCategories(Array.isArray(categoriesArray) ? categoriesArray : []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
      // Set empty arrays on error to prevent DataGrid issues
      setServices([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (service: ProfessionalService) => {
    setSelectedService(service);
    setVerificationNotes(service.verification_notes || '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedService(null);
    setVerificationNotes('');
  };

  const handleVerify = async (isVerified: boolean) => {
    if (!selectedService) return;

    try {
      const verifyData: VerifyServiceRequest = {
        is_verified: isVerified,
        verification_notes: verificationNotes,
        rejection_reason: isVerified ? undefined : rejectionReason,
      };
      await apiService.verifyService(selectedService.id, verifyData);
      handleCloseDialog();
      setRejectionReason('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update verification status');
    }
  };

  const handleRejectClick = () => {
    if (!selectedService) return;
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    await handleVerify(false);
    setRejectDialogOpen(false);
  };

  const handleUnverifyClick = () => {
    if (!selectedService) return;
    setUnverifyDialogOpen(true);
  };

  const handleUnverifyConfirm = async () => {
    if (!unverifyReason.trim()) {
      setError('Please provide a reason for unverifying this service');
      return;
    }
    try {
      const verifyData: VerifyServiceRequest = {
        is_verified: false,
        verification_notes: verificationNotes,
        rejection_reason: unverifyReason,
      };
      await apiService.verifyService(selectedService!.id, verifyData);
      setUnverifyDialogOpen(false);
      setUnverifyReason('');
      handleCloseDialog();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unverify service');
    }
  };

  const handleEditClick = (service: ProfessionalService) => {
    setSelectedService(service);
    setEditFormData({
      service_name: service.service_name,
      description: service.description,
      price: service.price.toString(),
      duration_minutes: service.duration_minutes,
      experience_years: service.experience_years || 0,
      specializations: service.specializations || [],
      service_category_id: service.service_category_id,
      is_available: service.is_available ?? true,
      is_featured: service.is_featured ?? false,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedService) return;

    try {
      setError('');
      await apiService.updateProfessionalService(selectedService.id, {
        service_name: editFormData.service_name,
        description: editFormData.description,
        price: parseFloat(editFormData.price),
        duration_minutes: editFormData.duration_minutes,
        experience_years: editFormData.experience_years,
        specializations: editFormData.specializations,
        is_available: editFormData.is_available,
        is_featured: editFormData.is_featured,
      });
      setEditDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update service');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteClick = async (service: ProfessionalService) => {
    if (!window.confirm(`Are you sure you want to delete "${service.service_name}"?`)) {
      return;
    }

    try {
      await apiService.deleteProfessionalService(service.id);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete service');
    }
  };

  const handleToggleFeatured = async (service: ProfessionalService, newStatus?: boolean) => {
    if (!service.is_verified) {
      setError('Service must be verified before it can be featured');
      return;
    }

    // Store the original service state for potential rollback
    const originalService = selectedService && selectedService.id === service.id 
      ? { ...selectedService } 
      : null;
    
    // Determine the new featured status
    // If newStatus is provided (from Switch), use it; otherwise toggle the current value
    const newFeaturedStatus = newStatus !== undefined ? newStatus : !service.is_featured;
    
    // Optimistically update the UI for better UX
    if (selectedService && selectedService.id === service.id) {
      setSelectedService({ ...selectedService, is_featured: newFeaturedStatus });
    }
    
    // Also optimistically update the services list
    setServices(prevServices => 
      prevServices.map(s => s.id === service.id ? { ...s, is_featured: newFeaturedStatus } : s)
    );

    try {
      setError('');
      
      const updatedService = await apiService.updateProfessionalService(service.id, {
        is_featured: newFeaturedStatus,
      });
      
      // Update the service in the local state with the response from server
      setServices(prevServices => 
        prevServices.map(s => s.id === service.id ? updatedService : s)
      );
      
      // Update selectedService with the server response
      if (selectedService && selectedService.id === service.id) {
        setSelectedService(updatedService);
      }
      
      // Refresh data to ensure consistency
      fetchData();
    } catch (err: any) {
      console.error('Error toggling featured status:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update featured status';
      setError(errorMessage);
      
      // Revert the optimistic update if the API call failed
      if (originalService) {
        setSelectedService(originalService);
      }
      
      // Revert the services list
      setServices(prevServices => 
        prevServices.map(s => s.id === service.id ? service : s)
      );
      
      setTimeout(() => setError(''), 5000);
    }
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Unknown';
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'service_name',
      headerName: 'Service Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <ServicesIcon />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'service_category_id',
      headerName: 'Category',
      width: 150,
      renderCell: (params) => getCategoryName(params.value),
    },
    {
      field: 'professional',
      headerName: 'Professional',
      width: 200,
      renderCell: (params) => params.value?.name || 'Unknown',
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 100,
      renderCell: (params) => `₹${params.value}`,
    },
    {
      field: 'duration_minutes',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => `${params.value} min`,
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 100,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2">{params.value}</Typography>
          <Typography variant="caption" color="text.secondary">
            ({params.row.total_reviews} reviews)
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_verified',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Verified' : 'Pending'}
          color={params.value ? 'success' : 'warning'}
          size="small"
        />
      ),
    },
    {
      field: 'is_available',
      headerName: 'Available',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'is_featured',
      headerName: 'Featured',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <FeaturedIcon /> : <UnfeaturedIcon />}
          label={params.value ? 'Featured' : 'Not Featured'}
          color={params.value ? 'warning' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 200,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            icon={<ViewIcon />}
            label="View Details"
            onClick={() => handleOpenDialog(params.row)}
            showInMenu={false}
          />,
        ];

        if (!params.row.is_verified) {
          actions.push(
            <GridActionsCellItem
              icon={<VerifyIcon />}
              label="Verify Service"
              onClick={() => handleOpenDialog(params.row)}
              showInMenu={false}
            />
          );
        }

        // Add featured toggle action (only for verified services)
        if (params.row.is_verified) {
          actions.push(
            <GridActionsCellItem
              icon={params.row.is_featured ? <UnfeaturedIcon /> : <FeaturedIcon />}
              label={params.row.is_featured ? 'Unfeature Service' : 'Feature Service'}
              onClick={() => handleToggleFeatured(params.row)}
              showInMenu={false}
            />
          );
          // Add unverify action (only for verified services)
          actions.push(
            <GridActionsCellItem
              icon={<UnverifyIcon />}
              label="Unverify Service"
              onClick={() => {
                setSelectedService(params.row);
                setUnverifyDialogOpen(true);
              }}
              showInMenu={false}
            />
          );
        }

        // Add edit action (admin can edit any service)
        actions.push(
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit Service"
            onClick={() => handleEditClick(params.row)}
            showInMenu={false}
          />
        );

        // Add delete action
        actions.push(
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete Service"
            onClick={() => handleDeleteClick(params.row)}
            showInMenu={false}
          />
        );

        return actions;
      },
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Professional Services</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant={viewMode === 'pending' ? 'contained' : 'outlined'}
            onClick={() => {
              setViewMode('pending');
            }}
          >
            Pending Verification
          </Button>
          <Button
            variant={viewMode === 'verified' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('verified')}
          >
            Verified (Public)
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={2} alignItems="center">
            {/* Search Field */}
            <Box flex={{ xs: 1, lg: 4 }}>
              <TextField
                fullWidth
                placeholder="Search services by name, description, professional, city, or specializations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Category Filter */}
            <Box flex={{ xs: 1, lg: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value as number)}
                >
                  <MenuItem value={0}>All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Verification Filter */}
            <Box flex={{ xs: 1, lg: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Verification</InputLabel>
                <Select
                  value={verificationFilter}
                  label="Verification"
                  onChange={(e) => setVerificationFilter(e.target.value as 'all' | 'verified' | 'pending')}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Availability Filter */}
            <Box flex={{ xs: 1, lg: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Availability</InputLabel>
                <Select
                  value={availabilityFilter}
                  label="Availability"
                  onChange={(e) => setAvailabilityFilter(e.target.value as 'all' | 'available' | 'unavailable')}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="unavailable">Unavailable</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Featured Filter */}
            <Box flex={{ xs: 1, lg: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Featured</InputLabel>
                <Select
                  value={featuredFilter}
                  label="Featured"
                  onChange={(e) => setFeaturedFilter(e.target.value as 'all' | 'featured' | 'not_featured')}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="featured">Featured</MenuItem>
                  <MenuItem value="not_featured">Not Featured</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Clear Filters Button */}
            <Box flex={{ xs: 1, lg: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>

          {/* Results Summary */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredServices.length} of {services.length} services
              {hasActiveFilters && ' (filtered)'}
              {viewMode === 'pending' && ' - Pending Verification'}
              {viewMode === 'verified' && ' - Verified Services'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {filteredServices.length === 0 && !isLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
              <ServicesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {hasActiveFilters ? 'No services found' : 'No services available'}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {hasActiveFilters 
                  ? 'Try adjusting your search or filter criteria'
                  : viewMode === 'pending' 
                    ? 'No services pending verification'
                    : 'No verified services available'}
              </Typography>
              {hasActiveFilters && (
                <Button 
                  variant="outlined" 
                  onClick={handleClearFilters}
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <DataGrid
              rows={Array.isArray(filteredServices) ? filteredServices : []}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              autoHeight
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Service Details - {selectedService?.service_name}
        </DialogTitle>
        <DialogContent>
          {selectedService && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Service Information
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {selectedService.service_name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Description:</strong> {selectedService.description}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Category:</strong> {getCategoryName(selectedService.service_category_id)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Price:</strong> ₹{selectedService.price}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Duration:</strong> {selectedService.duration_minutes} minutes
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Experience:</strong> {selectedService.experience_years} years
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Professional Information
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {selectedService.professional?.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Email:</strong> {selectedService.professional?.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Phone:</strong> {selectedService.professional?.phone}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>City:</strong> {selectedService.professional?.profile?.city}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Specializations
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedService.specializations.map((spec, index) => (
                    <Chip key={index} label={spec} size="small" />
                  ))}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Status
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                  <Chip
                    label={selectedService.is_verified ? 'Verified' : 'Pending Verification'}
                    color={selectedService.is_verified ? 'success' : 'warning'}
                  />
                  <Chip
                    label={selectedService.is_available ? 'Available' : 'Unavailable'}
                    color={selectedService.is_available ? 'success' : 'default'}
                  />
                  {selectedService.is_verified && (
                    <Chip
                      icon={selectedService.is_featured ? <FeaturedIcon /> : <UnfeaturedIcon />}
                      label={selectedService.is_featured ? 'Featured' : 'Not Featured'}
                      color={selectedService.is_featured ? 'warning' : 'default'}
                      onClick={() => handleToggleFeatured(selectedService)}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Grid>

              {selectedService.is_verified && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Featured Status
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedService.is_featured || false}
                        onChange={(e) => {
                          // Pass the new checked value directly to handleToggleFeatured
                          handleToggleFeatured(selectedService, e.target.checked);
                        }}
                        color="warning"
                        disabled={!selectedService.is_verified}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        {selectedService.is_featured ? <FeaturedIcon /> : <UnfeaturedIcon />}
                        <Typography>
                          {selectedService.is_featured ? 'Featured on Home Page' : 'Not Featured'}
                        </Typography>
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Featured services appear in the "Featured Professionals" section on the home page.
                  </Typography>
                </Grid>
              )}

              {!selectedService.is_verified && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Verification Notes"
                    multiline
                    rows={3}
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    placeholder="Add notes about the verification process..."
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {selectedService && !selectedService.is_verified && (
            <>
              <Button
                onClick={handleRejectClick}
                color="error"
                startIcon={<RejectIcon />}
              >
                Reject
              </Button>
              <Button
                onClick={() => handleVerify(true)}
                color="success"
                variant="contained"
                startIcon={<VerifyIcon />}
              >
                Verify
              </Button>
            </>
          )}
          {selectedService && selectedService.is_verified && (
            <Button
              onClick={handleUnverifyClick}
              color="warning"
              variant="outlined"
              startIcon={<UnverifyIcon />}
            >
              Unverify
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Service</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to reject the service: <strong>{selectedService?.service_name}</strong>
          </Alert>
          <TextField
            fullWidth
            label="Rejection Reason *"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a clear reason for rejection (e.g., Service description is incomplete, Price is not competitive, Missing required information)"
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setRejectionReason('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim()}
          >
            Reject Service
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unverify Reason Dialog */}
      <Dialog open={unverifyDialogOpen} onClose={() => setUnverifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Unverify Service</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to unverify the service: <strong>{selectedService?.service_name}</strong>
            <br />
            <small>This will move the service back to pending verification status and make it unavailable to patients.</small>
          </Alert>
          <TextField
            fullWidth
            label="Unverify Reason *"
            multiline
            rows={4}
            value={unverifyReason}
            onChange={(e) => setUnverifyReason(e.target.value)}
            placeholder="Please provide a clear reason for unverifying (e.g., Service needs re-review, Violation of guidelines, Temporary suspension for investigation)"
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUnverifyDialogOpen(false);
            setUnverifyReason('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleUnverifyConfirm}
            color="warning"
            variant="contained"
            disabled={!unverifyReason.trim()}
          >
            Confirm Unverify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Service Name *"
                value={editFormData.service_name}
                onChange={(e) => setEditFormData({ ...editFormData, service_name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description *"
                multiline
                rows={4}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={editFormData.service_category_id}
                  label="Category *"
                  onChange={(e) => setEditFormData({ ...editFormData, service_category_id: e.target.value as number })}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Price (₹) *"
                type="number"
                value={editFormData.price}
                onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Duration (minutes) *"
                type="number"
                value={editFormData.duration_minutes}
                onChange={(e) => setEditFormData({ ...editFormData, duration_minutes: parseInt(e.target.value) || 60 })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Experience (years)"
                type="number"
                value={editFormData.experience_years}
                onChange={(e) => setEditFormData({ ...editFormData, experience_years: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Specializations (comma-separated)"
                value={editFormData.specializations.join(', ')}
                onChange={(e) => {
                  const specs = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                  setEditFormData({ ...editFormData, specializations: specs });
                }}
                placeholder="e.g., Diabetes, Hypertension, Cardiology"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.is_available}
                    onChange={(e) => setEditFormData({ ...editFormData, is_available: e.target.checked })}
                    color="primary"
                  />
                }
                label="Service Available"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                When available, patients can book this service
              </Typography>
            </Grid>
            {selectedService?.is_verified && (
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.is_featured}
                      onChange={(e) => setEditFormData({ ...editFormData, is_featured: e.target.checked })}
                      color="warning"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      {editFormData.is_featured ? <FeaturedIcon /> : <UnfeaturedIcon />}
                      <Typography>Featured Service</Typography>
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Featured services appear on the home page
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          {selectedService?.is_verified && (
            <Button
              onClick={() => {
                setEditDialogOpen(false);
                setUnverifyDialogOpen(true);
              }}
              color="warning"
              variant="outlined"
              startIcon={<UnverifyIcon />}
            >
              Unverify
            </Button>
          )}
          <Button onClick={handleEditSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessionalServicesPage;
